import { useCallback, useEffect, useRef, useState, type JSX } from 'react';

/**
 * SectionRail — a long answer's own table of contents, beside the answer.
 *
 * Breww already parses every heading in a reply and then throws that structure
 * away at render time, leaving the reader to scroll a long answer linearly. This
 * surfaces it: the sections, where you are in them, and one click to any of them.
 *
 * WHEN IT APPEARS IS MEASURED, NOT GUESSED. An earlier version required three
 * top-level headings and gated on viewport breakpoints — both numbers invented
 * from whichever answer happened to be on screen while it was written. The result
 * was a feature that appeared and vanished for reasons the reader could not see.
 * Both conditions are now facts about this answer in this window:
 *
 *   • Does the answer actually overflow the pane? If you can see all of it, an
 *     outline is decoration. Navigation is only useful when there is something
 *     off-screen to navigate to.
 *   • Does the rail physically fit in the margin? `.chat` clips horizontally, so
 *     the question is the width of the ACTUAL gutter beside the reading column —
 *     not the width of the viewport, which is only a proxy for it.
 *
 * The one remaining floor, two entries, is not a threshold: an outline with a
 * single item cannot navigate anywhere.
 */

interface Section { id: string; text: string; level: number }

/** An outline needs somewhere to go. One entry is not navigation. */
const MIN_SECTIONS = 2;

/** The rail's own dimensions — the gutter must hold one of these two forms.
 *  These are the component's size, not tuning knobs. */
const FULL_FORM_PX = 188;   // 168 label column + 20 gap
const TRACK_FORM_PX = 38;   // 22 position track + 16 gap

type Form = 'none' | 'track' | 'full';

interface SectionRailProps {
  /** Re-scan when the settled body changes (streamed → final, edit, reload). */
  body: string;
  /** Suppressed while streaming: headings are still arriving, and an outline
   *  that reshuffles under the cursor is worse than no outline. */
  streaming?: boolean;
}

export function SectionRail({ body, streaming }: SectionRailProps): JSX.Element {
  const navRef = useRef<HTMLElement | null>(null);
  const [sections, setSections] = useState<Section[]>([]);
  const [activeId, setActiveId] = useState<string>('');
  const [form, setForm] = useState<Form>('none');

  /** Does this answer overflow the pane, and does the rail fit beside it? Both
   *  are read from the live layout, so a window resize re-answers them. */
  const measure = useCallback((): void => {
    const message = navRef.current?.closest('.msg--assistant') as HTMLElement | null;
    const scroller = navRef.current?.closest('.chat') as HTMLElement | null;
    if (!message || !scroller) { setForm('none'); return; }

    const msg = message.getBoundingClientRect();
    const pane = scroller.getBoundingClientRect();

    // Off-screen content is the whole reason to offer navigation.
    const overflowsPane = msg.height > scroller.clientHeight;
    if (!overflowsPane) { setForm('none'); return; }

    // The real constraint: space to the RIGHT of the reading column, inside the
    // clipping pane. A wide viewport with a wide sidebar can still have no gutter.
    const gutter = pane.right - msg.right;
    setForm(gutter >= FULL_FORM_PX ? 'full' : gutter >= TRACK_FORM_PX ? 'track' : 'none');
  }, []);

  // Collect the answer's headings once the body has settled.
  useEffect(() => {
    if (streaming) { setSections([]); return undefined; }
    const message = navRef.current?.closest('.msg--assistant');
    const bodyEl = message?.querySelector('.msg__body');
    if (!bodyEl) { setSections([]); return undefined; }

    // EVERY heading level, at ANY depth, minus the ones that belong to a block
    // rather than to the answer's structure.
    //
    // Two earlier versions of this were arbitrary in ways the reader could see.
    // `:scope >` meant a section wrapped in a list — a FORMATTING slip — hid the
    // whole rail. Restricting to h1–h3 meant the rail's existence depended on
    // which heading LEVEL the model happened to choose: a plan emitted with
    // "####" headings had four obvious sections and no outline. Neither is a
    // property of the answer; both are accidents of markup.
    const raw: Array<{ el: Element; text: string; tag: number }> = [];
    bodyEl.querySelectorAll('h1, h2, h3, h4, h5, h6').forEach((el) => {
      if (el.closest('.callout, blockquote, pre, .code-block')) return;
      const text = (el.textContent ?? '').trim();
      if (text) raw.push({ el, text, tag: Number(el.tagName.slice(1)) });
    });

    // Normalise depth to what is actually PRESENT: the shallowest heading in this
    // answer becomes level 1 whatever its tag, so an answer written entirely in
    // "###" indents identically to one written in "##". The rail shows the
    // answer's structure, not its markup.
    const shallowest = raw.length ? Math.min(...raw.map((r) => r.tag)) : 1;
    const found: Section[] = raw.map((r, i) => {
      if (!r.el.id) r.el.id = `sec-${i}-${r.text.length}`;
      return { id: r.el.id, text: r.text, level: Math.min(3, r.tag - shallowest + 1) };
    });
    setSections(found.length >= MIN_SECTIONS ? found : []);
    return undefined;
  }, [body, streaming]);

  // Re-measure whenever the answer or the window changes shape.
  useEffect(() => {
    if (sections.length === 0) { setForm('none'); return undefined; }
    measure();
    const message = navRef.current?.closest('.msg--assistant');
    const scroller = navRef.current?.closest('.chat');
    const ro = new ResizeObserver(() => measure());
    if (message) ro.observe(message);
    if (scroller) ro.observe(scroller);
    return () => ro.disconnect();
  }, [sections, measure]);

  // Reading position. The chat pane is the scroller (not the window), so the
  // observer must use it as root or nothing ever intersects.
  useEffect(() => {
    if (sections.length === 0 || form === 'none') return undefined;
    const message = navRef.current?.closest('.msg--assistant');
    const scroller = navRef.current?.closest('.chat') as HTMLElement | null;
    if (!message || !scroller) return undefined;

    // Bias the band toward the top of the pane: the "current" section is the one
    // you are reading at the top of the viewport, not the one in the middle.
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((e) => e.isIntersecting);
        if (visible.length === 0) return;
        const top = visible.reduce((a, b) =>
          (a.boundingClientRect.top <= b.boundingClientRect.top ? a : b));
        setActiveId(top.target.id);
      },
      { root: scroller, rootMargin: '-8% 0px -70% 0px', threshold: 0 },
    );
    for (const s of sections) {
      const el = message.querySelector(`#${CSS.escape(s.id)}`);
      if (el) observer.observe(el);
    }
    return () => observer.disconnect();
  }, [sections, form]);

  const jump = (id: string): void => {
    const message = navRef.current?.closest('.msg--assistant');
    const scroller = navRef.current?.closest('.chat') as HTMLElement | null;
    const el = message?.querySelector(`#${CSS.escape(id)}`);
    if (!el || !scroller) return;
    // Offset by hand rather than scrollIntoView: the heading should land with a
    // little air above it, not flush against the top edge of the pane.
    const top = el.getBoundingClientRect().top
      - scroller.getBoundingClientRect().top
      + scroller.scrollTop - 20;
    scroller.scrollTo({ top, behavior: 'smooth' });
    setActiveId(id);
  };

  const show = sections.length >= MIN_SECTIONS && form !== 'none';

  // The element always mounts (hidden when there is nothing to show) so the
  // effects above have a node to measure the message and pane from.
  return (
    <nav
      className={`sec-rail sec-rail--${form}`}
      ref={navRef}
      aria-label="Sections in this answer"
      {...(show ? {} : { hidden: true })}
    >
      {show ? (
        <div className="sec-rail__inner">
          <span className="sec-rail__title">On this answer</span>
          <ul className="sec-rail__list">
            {sections.map((s) => (
              <li key={s.id}>
                <button
                  type="button"
                  className={`sec-rail__item sec-rail__item--l${s.level}${s.id === activeId ? ' is-active' : ''}`}
                  onClick={() => jump(s.id)}
                  // In the narrow TRACK form the label is visually hidden, so the
                  // native tooltip carries it. Native on purpose: a styled popover
                  // would be clipped by the chat pane's overflow.
                  title={s.text}
                  {...(s.id === activeId ? { 'aria-current': 'true' as const } : {})}
                >
                  {s.text}
                </button>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </nav>
  );
}
